import { useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthLayout } from '@/layouts/AuthLayout';
import { clearSession, startSession } from '@/lib/session';

import styles from './LoginPage.module.scss';
import { platformAcceptInvitation } from '../api/platform-auth.api';

/** Mismo mínimo que el API (`PLATFORM_PASSWORD_MIN_LENGTH`). */
export const INVITATION_PASSWORD_MIN_LENGTH = 12;

/**
 * Lee el token del fragmento (`#token=`) y lo quita de la barra de direcciones: no queda en el
 * historial ni se envía al servidor en la carga de la página.
 */
function takeTokenFromHash(): string {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = params.get('token') ?? '';
  if (window.location.hash) {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
  return token;
}

/**
 * OS-1717 — aceptar la invitación de administrador: definir la contraseña y abrir la sesión
 * (MFA deshabilitado temporalmente). El token es de un solo uso.
 */
export function AcceptInvitationPage(): JSX.Element {
  const navigate = useNavigate();
  const [token] = useState(takeTokenFromHash);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const tooShort = password.length > 0 && password.length < INVITATION_PASSWORD_MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (password.length < INVITATION_PASSWORD_MIN_LENGTH || password !== confirm) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { accessToken, email } = await platformAcceptInvitation(token, password);
      // Una sesión previa en este navegador no debe mezclarse con la del admin nuevo.
      clearSession();
      // MFA deshabilitado temporalmente: la invitación aceptada abre la sesión completa.
      startSession(accessToken, email);
      void navigate('/dashboard', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="Invitación no válida" subtitle="OpsSynk Platform Console">
        <p className={styles.error} role="alert">
          El link de invitación está incompleto. Ábrelo de nuevo desde el mensaje que recibiste o pide
          uno nuevo a un administrador Super.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Activa tu acceso" subtitle="Define tu contraseña para ingresar">
      <form className={styles.form} onSubmit={(e) => void handleSubmit(e)}>
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="new-password">
            Contraseña nueva
          </label>
          <input
            id="new-password"
            type="password"
            className={styles.input}
            value={password}
            minLength={INVITATION_PASSWORD_MIN_LENGTH}
            maxLength={128}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            aria-invalid={tooShort}
            aria-describedby="password-hint"
          />
          <span id="password-hint" className={styles.label}>
            Mínimo {INVITATION_PASSWORD_MIN_LENGTH} caracteres. No uses tu email.
          </span>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="confirm-password">
            Confirma la contraseña
          </label>
          <input
            id="confirm-password"
            type="password"
            className={styles.input}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            aria-invalid={mismatch}
          />
          {mismatch ? (
            <span className={styles.error} role="alert">
              Las contraseñas no coinciden.
            </span>
          ) : null}
        </div>

        <button
          type="submit"
          className={styles.submitBtn}
          disabled={loading || password.length < INVITATION_PASSWORD_MIN_LENGTH || password !== confirm}
        >
          {loading ? 'Guardando…' : 'Activar acceso'}
        </button>
      </form>
    </AuthLayout>
  );
}
